import { CircularProgress } from '@mui/material';
import moment from 'moment-timezone';
import * as React from 'react';
import { useTranslation } from 'react-i18next';

import {
  areSameMonth,
  DateAttributes,
  getWeeksForMonth,
  isCurrentMonth,
  Week,
} from 'lib/util/datePickerHelpers';
import { getMonthYearFormat } from 'lib/util/i18nHelpers';

import { RequestIcon } from './RequestIcon';
import { InstantIcon } from './InstantIcon';

interface Props {
  month: string;
  loading: boolean;
  attrByDateMap?: { [date: string]: Omit<DateAttributes, 'isLimitedAvailability'> };
  onPrevMonthClick?: () => void;
  onNextMonthClick?: () => void;
  onDateClick: (date: string) => void;
}

export const ProductGroupSingleMonthDatePicker = ({
  loading,
  month,
  attrByDateMap,
  onPrevMonthClick,
  onNextMonthClick,
  onDateClick,
}: Props) => {
  const { i18n, t } = useTranslation();

  const locale = i18n.language;

  const weeks: Week[] = getWeeksForMonth(month);

  return (
    <div className="widget-multi-calendar__month">
      {loading && (
        <CircularProgress
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            marginTop: '-20px',
            marginLeft: '-20px',
          }}
        />
      )}
      <div className="widget-multi-calendar__month__nav">
        <p className="widget-multi-calendar__month__nav__title">
          {moment(month).locale(locale).format(getMonthYearFormat(locale))}
        </p>
        {onPrevMonthClick && (
          <button
            disabled={isCurrentMonth(month)}
            className="widget-multi-calendar__month__nav__button prev"
            onClick={onPrevMonthClick}
          />
        )}
        {onNextMonthClick && (
          <button
            className="widget-multi-calendar__month__nav__button next"
            onClick={onNextMonthClick}
          />
        )}
      </div>
      <ul className="widget-multi-calendar__month__title">
        <li>{t('Sun')}</li>
        <li>{t('Mon')}</li>
        <li>{t('Tue')}</li>
        <li>{t('Wed')}</li>
        <li>{t('Thu')}</li>
        <li>{t('Fri')}</li>
        <li>{t('Sat')}</li>
      </ul>
      {weeks.map((week, idx) => (
        <ul className="widget-multi-calendar__month__week" key={idx}>
          {week.days.map((day) => {
            if (!areSameMonth(month, day)) {
              return <li key={day} className="blank" />;
            }

            const status = attrByDateMap?.[day]?.status;
            const hasPromo = attrByDateMap?.[day]?.hasPromo;
            if (!status) {
              return (
                <li key={day} className="past">
                  <p className="widget-multi-calendar__month__week__date">
                    {moment(day).format('D')}
                  </p>
                </li>
              );
            }

            if (status === 'request') {
              return (
                <li key={day} className="request" onClick={() => onDateClick(day)}>
                  <p className="widget-multi-calendar__month__week__date">
                    {moment(day).format('D')}
                  </p>
                  <p className="widget-multi-calendar__month__week__ic">
                    <RequestIcon />
                  </p>
                  {hasPromo && (
                    <p className="widget-multi-calendar__month__week__promo">
                      <img src="/static/images/ic_promo.svg" />
                    </p>
                  )}
                </li>
              );
            }

            if (status === 'instant') {
              return (
                <li key={day} className="instant" onClick={() => onDateClick(day)}>
                  <p className="widget-multi-calendar__month__week__date">
                    {moment(day).format('D')}
                  </p>
                  <p className="widget-multi-calendar__month__week__ic">
                    <InstantIcon />
                  </p>
                  {hasPromo && (
                    <p className="widget-multi-calendar__month__week__promo">
                      <img src="/static/images/ic_promo.svg" />
                    </p>
                  )}
                </li>
              );
            }

            if (status === 'fully_booked') {
              return (
                <li key={day}>
                  <p className="widget-multi-calendar__month__week__date">
                    {moment(day).format('D')}
                  </p>
                  <p className="widget-multi-calendar__month__week__ic">
                    <img src="/static/images/ic_fullybooked.svg" alt="closed" />
                  </p>
                  <p className="widget-multi-calendar__month__week__price" />
                </li>
              );
            }

            // All other statuses interpreted as 'closed'
            return (
              <li key={day}>
                <p className="widget-multi-calendar__month__week__date">
                  {moment(day).format('D')}
                </p>
                <p className="widget-multi-calendar__month__week__ic">
                  <img src="/static/images/ic_ng.svg" alt="closed" />
                </p>
                <p className="widget-multi-calendar__month__week__price" />
              </li>
            );
          })}
        </ul>
      ))}
      {
        /* pad with two extra rows if there are only 4 weeks covered by the month's dates */
        weeks.length === 4 && (
          <>
            <ul className="widget-multi-calendar__month__week" />
            <ul className="widget-multi-calendar__month__week" />
          </>
        )
      }
      {
        /* pad with one extra row if there are only 5 weeks covered by the month's dates */
        weeks.length === 5 && <ul className="widget-multi-calendar__month__week" />
      }
    </div>
  );
};
